import { useState } from 'react';
import {
  Card, CardHeader, CardBody, Button, Modal, Field, Input, Select,
  Table, THead, TBody, TR, TH, TD, StatusBadge, EmptyState, ErrorNotice, Loading,
  ConfirmDialog, CopyField, CopyButton,
} from '../../components/ui/index.js';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';
import { formatTimestamp, formatNumber } from '../../lib/format.js';
import { useAuth } from '../../lib/auth.jsx';
import { KeyRound } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

/**
 * AK-7: "The API keys screen includes a working curl example for creating a
 * payment. A developer should be able to make their first successful call
 * without opening the docs."
 *
 * The example uses the key that was just issued, so it is genuinely runnable —
 * a placeholder like <YOUR_KEY> is where most first calls fail.
 */
function CurlExample({ secretKey }) {
  const apiBase = `${window.location.origin.replace(/:\d+$/, ':4000')}/api/v1`;
  const key = secretKey ?? 'sk_test_your_key_here';

  const command = `curl ${apiBase}/payments \\
  -u ${key}: \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -d amount=1050 \\
  -d currency=USD \\
  -d orderId=order-1 \\
  -d "customer[email]=customer@example.com"`;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-ink">Create your first payment</p>
        <CopyButton value={command} label="Copy command" />
      </div>
      <pre className="overflow-x-auto rounded-md border border-line bg-slate-900 p-3.5 text-xs leading-relaxed text-slate-100">
        <code>{command}</code>
      </pre>
      <p className="mt-2 text-xs text-muted">
        {secretKey
          ? 'This uses the key above, so it will work as-is. The response contains a checkoutUrl to send to your customer.'
          : 'Create a key to get a runnable version of this command.'}
        {' '}Amounts are integers in the smallest currency unit — 1050 is $10.50.
      </p>
    </div>
  );
}

export function ApiKeysPage() {
  const toast = useToast();
  const { refreshMerchant } = useAuth();
  const { data, error, loading, refetch } = useResource('/developers/api-keys');

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', environment: 'sandbox' });
  const [issued, setIssued] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [rotating, setRotating] = useState(null);

  const create = useMutation(async () => {
    const result = await http.post('/developers/api-keys', {
      name: form.name || 'Default key',
      environment: form.environment,
    });
    setIssued(result);
    setCreateOpen(false);
    setForm({ name: '', environment: 'sandbox' });
    refetch();
    refreshMerchant();
  });

  const revoke = useMutation(async (key) => {
    await http.delete(`/developers/api-keys/${key.id}`);
    toast.success('Key revoked', { description: 'Any request using it now fails immediately.' });
    setRevoking(null);
    refetch();
  });

  const rotate = useMutation(async (key) => {
    const result = await http.post(`/developers/api-keys/${key.id}/rotate`);
    setIssued(result);
    setRotating(null);
    refetch();
  });

  return (
    <>
      <PageHero
        icon={KeyRound}
        chip="Developers"
        title="API keys"
        blurb="Your server authenticates with a secret key. Never put one in frontend code."
        actions={<Button onClick={() => setCreateOpen(true)}>Create key</Button>}
      />

      <Card className="mb-6">
        <CardHeader title="Quick start" blurb="A working request, using your own key" />
        <CardBody><CurlExample secretKey={issued?.secretKey} /></CardBody>
      </Card>

      <Card>
        <CardHeader title="Your keys" />
        {loading && <Loading label="Loading keys…" />}
        {error && <ErrorNotice error={error} onRetry={refetch} className="m-4" />}

        {!loading && !error && data?.length === 0 && (
          <EmptyState
            title="No API keys yet"
            blurb="A key lets your server create payments and issue refunds. The secret is shown once, when you create it."
            action={<Button onClick={() => setCreateOpen(true)}>Create your first key</Button>}
          />
        )}

        {!loading && !error && data?.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH><TH>Key</TH><TH>Environment</TH><TH>Status</TH>
                <TH align="right">Used</TH><TH align="right">Last used</TH><TH align="right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {data.map((key) => (
                <TR key={key.id}>
                  <TD className="font-medium text-ink">{key.name}</TD>
                  {/* AK-5: a masked secret, and nothing more. */}
                  <TD className="font-mono text-xs text-ink-soft">{key.secretKeyMasked}</TD>
                  <TD><StatusBadge status={key.environment} size="sm" /></TD>
                  <TD><StatusBadge status={key.status} size="sm" /></TD>
                  <TD align="right" className="tabular text-sm">{formatNumber(key.usageCount)}</TD>
                  <TD align="right" className="text-xs text-muted">{formatTimestamp(key.lastUsedAt)}</TD>
                  <TD align="right">
                    {key.status === 'active' && (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setRotating(key)}>Rotate</Button>
                        <Button size="sm" variant="ghost" onClick={() => setRevoking(key)}>Revoke</Button>
                      </div>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/* Create */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create an API key"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={create.loading}>Cancel</Button>
            <Button onClick={() => create.mutate().catch(() => {})} loading={create.loading}>Create key</Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); create.mutate().catch(() => {}); }}>
          {create.error && <ErrorNotice error={create.error} title="Could not create the key" />}
          <Field label="Name" hint="For your own reference — which server or service uses it.">
            {(props) => <Input {...props} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Production server" autoFocus />}
          </Field>
          <Field
            label="Environment"
            hint="A live key only works against a live provider connection. We refuse the mismatch rather than reporting a test charge as real."
          >
            {(props) => (
              <Select {...props} value={form.environment} onChange={(e) => setForm((f) => ({ ...f, environment: e.target.value }))}>
                <option value="sandbox">Sandbox (test)</option>
                <option value="production">Production (live)</option>
              </Select>
            )}
          </Field>
        </form>
      </Modal>

      {/* AK-2: shown exactly once, with a clear warning. */}
      <Modal
        open={Boolean(issued)}
        onClose={() => setIssued(null)}
        title="Copy your secret key"
        blurb="This is the only time it will be shown."
        footer={<Button onClick={() => setIssued(null)}>I&rsquo;ve saved it</Button>}
      >
        <div className="space-y-4">
          <CopyField label="Secret key" value={issued?.secretKey} />
          <CopyField label="Publishable key" value={issued?.apiKey?.publishableKey} hint="Identifies your account. Not a secret." />
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5">
            <p className="text-sm font-medium text-amber-700">We store only a hash</p>
            <p className="mt-1 text-xs text-ink-soft">
              We cannot show this key again, and we cannot recover it for you. If you lose it, rotate the key
              to get a replacement.
            </p>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(revoking)}
        onClose={() => setRevoking(null)}
        onConfirm={() => revoke.mutate(revoking).catch(() => {})}
        loading={revoke.loading}
        title={`Revoke "${revoking?.name}"?`}
        consequence="This takes effect immediately. Any server still using this key will start getting 401 errors on its very next request, including in the middle of a checkout."
        confirmLabel="Revoke key"
      />

      <ConfirmDialog
        open={Boolean(rotating)}
        onClose={() => setRotating(null)}
        onConfirm={() => rotate.mutate(rotating).catch(() => {})}
        loading={rotate.loading}
        variant="primary"
        title={`Rotate "${rotating?.name}"?`}
        consequence="We will issue a replacement key and revoke this one in the same action. Deploy the new key before rotating, or requests will fail in the gap."
        confirmLabel="Rotate key"
      />
    </>
  );
}

export default ApiKeysPage;
